import { useGetProposal } from '@/api/queries/proposal/useGetProposal';
import { FC } from 'react';
import { useHistory } from 'react-router-dom';
import EditSentProposalModal from './EditSentProposalModal';
import MarkProposalAsAcceptedModal from './MarkProposalAsAcceptedModal';
import MarkProposalAsDeclinedModal from './MarkProposalAsDeclinedModal';
import RemoveProposalAcceptanceModal from './RemoveProposalAcceptanceModal';
import { type ProposalConfirmation } from './useProposalLifecycleActions';

type ProposalLifecycleModalsProps = {
  /** Public id of the Proposal — the route param. */
  proposalId: string;
  /** Public id of the parent Project — the route param. */
  projectId: string;
  openConfirmation: ProposalConfirmation | null;
  onCloseConfirmation: () => void;
};

/**
 * The confirmations behind the send panel's lifecycle actions. Kept mounted, as
 * Ambrosia modals require, and driven by the open state
 * `useProposalLifecycleActions` holds.
 */
const ProposalLifecycleModals: FC<ProposalLifecycleModalsProps> = (props) => {
  const { proposalId, projectId, openConfirmation, onCloseConfirmation } =
    props;

  const history = useHistory();
  const { proposal } = useGetProposal(proposalId);

  // The resolved schedule normalises a fixed-amount and a percentage deposit
  // into the same stages, so it is the one place to ask. A configured zero is
  // not a deposit — the backend raises no invoice for it.
  const hasDeposit = !!proposal?.effectivePaymentSchedule?.stages.some(
    (stage) =>
      stage.__typename === 'DerivedDepositStage' && stage.value.value > 0
  );

  return (
    <>
      <MarkProposalAsAcceptedModal
        isOpen={openConfirmation === 'markAsAccepted'}
        onClose={onCloseConfirmation}
        proposalId={proposalId}
        projectId={projectId}
        hasDeposit={hasDeposit}
      />

      <MarkProposalAsDeclinedModal
        isOpen={openConfirmation === 'markAsDeclined'}
        onClose={onCloseConfirmation}
        proposalId={proposalId}
        projectId={projectId}
      />

      <RemoveProposalAcceptanceModal
        isOpen={openConfirmation === 'removeAccepted'}
        onClose={onCloseConfirmation}
        proposalId={proposalId}
        numericProposalId={proposal?.id}
        projectId={projectId}
      />

      <EditSentProposalModal
        isOpen={openConfirmation === 'replace'}
        onClose={onCloseConfirmation}
        proposalId={proposalId}
        projectId={projectId}
        onReplaced={(replacementProposalId) =>
          history.push(
            `/projects/${projectId}/proposals/${replacementProposalId}`
          )
        }
      />
    </>
  );
};

export default ProposalLifecycleModals;
